import React from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  Box,
  VStack,
  Heading,
  SimpleGrid,
  Flex,
  IconButton,
  Text,
} from '@chakra-ui/react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { CustomInput } from '../../custom/CustomInput';
import { CustomSelect } from '../../custom/CustomSelect';
import { CustomButton } from '../../custom/CustomButton';

const CorporateOfficers = ({ onNext, onBack, loading, initialValues }) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: initialValues || {
      officers: [
        {
          officerTitle: '',
          jobTitle: '',
          rights: '',
          holding: '',
          fullName: '',
          gender: '',
          dob: '',
          mobile: '',
          email: '',
          primaryNationality: '',
          secondaryNationality: '',
          countryOfResidence: '',
          passportNumber: '',
          expiryDate: '',
          fullAddress: '',
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'officers',
  });

  const onSubmit = (data) => {
    onNext(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={8} align="stretch">
        <Heading size="md" color="blue.600">
          Corporate Officers
        </Heading>
        <Text color="gray.600">
          Add Directors, Shareholders, and Authorized Persons.
        </Text>

        {fields.map((field, index) => (
          <Box
            key={field.id}
            p={6}
            borderWidth="1px"
            borderRadius="md"
            position="relative"
            bg="gray.50"
          >
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="sm">Officer #{index + 1}</Heading>
              {fields.length > 1 && (
                <IconButton
                  colorPalette="red"
                  variant="ghost"
                  onClick={() => remove(index)}
                  aria-label="Remove officer"
                >
                  <FaTrash />
                </IconButton>
              )}
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} columnGap={8}>
              <CustomSelect
                label="Officer Title"
                error={errors.officers?.[index]?.officerTitle}
                required
                placeholder="Select title"
                {...register(`officers.${index}.officerTitle`, { required: 'Required' })}
              >
                  <option value="Director">Director</option>
                  <option value="Shareholder">Shareholder</option>
                  <option value="Authorize Person">Authorize Person</option>
              </CustomSelect>

              <CustomInput
                label="Job Title / Designation"
                error={errors.officers?.[index]?.jobTitle}
                required
                placeholder="e.g., CEO"
                {...register(`officers.${index}.jobTitle`, { required: 'Required' })}
              />

              <CustomSelect
                label="Rights"
                error={errors.officers?.[index]?.rights}
                required
                placeholder="Select rights"
                {...register(`officers.${index}.rights`, { required: 'Required' })}
              >
                  <option value="Full Access">Full Access</option>
                  <option value="Trade Only">Trade Only</option>
                  <option value="Information Only">Information Only</option>
              </CustomSelect>

              <CustomInput
                label="Holding (%)"
                error={errors.officers?.[index]?.holding}
                type="number"
                placeholder="e.g., 50"
                {...register(`officers.${index}.holding`)}
              />

              <CustomInput
                label="Full Name"
                error={errors.officers?.[index]?.fullName}
                required
                placeholder="Enter full name"
                {...register(`officers.${index}.fullName`, { required: 'Required' })}
              />

              <CustomSelect
                label="Gender"
                error={errors.officers?.[index]?.gender}
                required
                placeholder="Select gender"
                {...register(`officers.${index}.gender`, { required: 'Required' })}
              >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
              </CustomSelect>

              <CustomInput
                label="Date of Birth"
                error={errors.officers?.[index]?.dob}
                required
                type="date"
                {...register(`officers.${index}.dob`, { required: 'Required' })}
              />

              <CustomInput
                label="Mobile"
                error={errors.officers?.[index]?.mobile}
                required
                type="tel"
                placeholder="Enter mobile"
                {...register(`officers.${index}.mobile`, { required: 'Required' })}
              />

              <CustomInput
                label="Official Email"
                error={errors.officers?.[index]?.email}
                required
                type="email"
                placeholder="Enter email"
                {...register(`officers.${index}.email`, { required: 'Required' })}
              />

              <CustomInput
                label="Primary Nationality"
                error={errors.officers?.[index]?.primaryNationality}
                required
                placeholder="Enter nationality"
                {...register(`officers.${index}.primaryNationality`, { required: 'Required' })}
              />

              <CustomInput
                label="Secondary Nationality"
                placeholder="Optional"
                {...register(`officers.${index}.secondaryNationality`)}
              />

              <CustomInput
                label="Country of Residence"
                error={errors.officers?.[index]?.countryOfResidence}
                required
                placeholder="Enter country"
                {...register(`officers.${index}.countryOfResidence`, { required: 'Required' })}
              />

              <CustomInput
                label="Passport Number"
                error={errors.officers?.[index]?.passportNumber}
                required
                placeholder="Enter passport no."
                uppercase
                {...register(`officers.${index}.passportNumber`, { 
                  required: 'Required',
                  validate: (value) => {
                    // Standard passport number validation: alphanumeric, 6-9 characters
                    const passportRegex = /^[A-Z0-9]{6,9}$/;
                    if (!passportRegex.test(value)) {
                      return 'Passport number must be 6-9 alphanumeric characters';
                    }
                    return true;
                  }
                })}
              />

              <CustomInput
                label="Passport Expiry Date"
                error={errors.officers?.[index]?.expiryDate}
                required
                type="date"
                {...register(`officers.${index}.expiryDate`, { required: 'Required' })}
              />

              <Box gridColumn={{ lg: 'span 3' }}>
                  <CustomInput
                    label="Full Address"
                    error={errors.officers?.[index]?.fullAddress}
                    required
                    placeholder="Enter residential address"
                    {...register(`officers.${index}.fullAddress`, { required: 'Required' })}
                  />
              </Box>
            </SimpleGrid>
          </Box>
        ))}

        <CustomButton
          onClick={() =>
            append({
              officerTitle: '',
              jobTitle: '',
              rights: '',
              holding: '',
              fullName: '',
              gender: '',
              dob: '',
              mobile: '',
              email: '',
              primaryNationality: '',
              secondaryNationality: '',
              countryOfResidence: '',
              passportNumber: '',
              expiryDate: '',
              fullAddress: '',
            })
          }
          variant="outline"
          colorScheme="blue"
        >
          <FaPlus style={{ marginRight: '8px' }} />
          Add Another Officer
        </CustomButton>

        <Flex justify="space-between" mt={8}>
          <CustomButton onClick={onBack} variant="ghost" color="gray.600">
            Back
          </CustomButton>
          <CustomButton
            type="submit"
            colorScheme="blue"
            size="lg"
            isLoading={loading}
            loadingText="Saving..."
          >
            Next: Documents Verification
          </CustomButton>
        </Flex>
      </VStack>
    </form>
  );
};

export default CorporateOfficers;
